import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { clearPersistedFilters } from "@/lib/persistedFilters";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 119e — routes belonging to apps this user may not open (inactive apps
  // are closed to everyone, super admins included).
  const [blockedRoutes, setBlockedRoutes] = useState([]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Remembered filters belong to one person in this tab — a different sign-in starts clean.
    if (!user?.user_id) return;
    const owner = `${user.user_id}:${user.masqueraded_by || ""}`;
    try {
      if (window.sessionStorage.getItem("cf-owner") !== owner) {
        clearPersistedFilters();
        window.sessionStorage.setItem("cf-owner", owner);
      }
    } catch { /* storage blocked */ }
  }, [user?.user_id, user?.masqueraded_by]);

  useEffect(() => {
    if (!user) return;
    api.get("/apps/blocked-routes")
      .then((r) => setBlockedRoutes(r.data?.blocked || []))
      .catch(() => setBlockedRoutes([]));
  }, [user]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    clearPersistedFilters();
    setUser(false);
    window.location.href = "/login";
  };

  // Access model v2: `has("settings_*.view")` matches any settings tab view.
  const has = (perm) => {
    if (!user || !Array.isArray(user.permissions)) return false;
    if (perm.includes("*")) {
      const re = new RegExp("^" + perm.split("*").map((x) => x.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*") + "$");
      return user.permissions.some((p) => re.test(p));
    }
    return user.permissions.includes(perm);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, has, blockedRoutes }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
